import { Alert, Anchor, Button, Card, Divider, Modal, Stack, Text, Textarea } from "@mantine/core";
import { IMeeting } from "@mctypes/meeting.types";
import { IconCircleCheck, IconSend } from "@tabler/icons-react";
import moment from "moment";
import "moment/dist/locale/nb";
import React, { useState } from "react";
import { FailedAlert, McTitle } from "../../../../../components";
import { useAppSelector } from "../../../../../hooks";
import { useSendMeetingAgendaByMailMutation } from "../../meetingRTK";

const SendMeetingAgendaByMailModal: React.FC<{
  close: () => void;
  meeting: IMeeting.MeetingPopulated;
  altTitle?: string;
}> = ({ close, meeting, altTitle }) => {
  const user = useAppSelector((state) => state.globalReducer.user);
  const day = moment(meeting.dateOfMeeting).date();
  const month = moment(meeting.dateOfMeeting).format("MMMM");
  const meetingSeries = meeting?.meetingSerie as IMeeting.MeetingSerie;
  const modalTitle = altTitle ? altTitle : "agenda";
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  const capitalizedModalTitle = capitalizeFirstLetter(modalTitle);

  const [emailContent, setEmailContent] = useState<string>(
    "Hei [møtedeltakere].\n\n" +
      `Under finner du ${modalTitle} for møtet ` +
      day +
      " " +
      month +
      (meetingSeries ? ",\n" + "en del av møteserie: " + meetingSeries.name + ".\n" : ".\n") +
      "\nMvh " +
      user.name,
  );
  const [sendMeetingAgnedaByMail, { isLoading: sendMeetingAgendaMail, error: sendEmailError, isSuccess }] =
    useSendMeetingAgendaByMailMutation();

  const emailSubject = `${capitalizedModalTitle} for møtet ${meeting.name}`;

  const url = window.location.href;

  return (
    <Modal opened onClose={close} title={`Distribuer ${modalTitle} på e-post`} size="lg" padding="md" radius="md">
      <Card p="sm" bg={"#fafafb"} bd={"1px solid var(--mantine-color-warmgray-2)"}>
        <Stack gap="sm">
          {sendEmailError != null && <FailedAlert />}
          {isSuccess === true && (
            <Alert color="green" fw={500} icon={<IconCircleCheck size={20} />}>
              E-posten ble sendt
            </Alert>
          )}
          {isSuccess !== true && sendEmailError == null && (
            <Stack gap="sm">
              <McTitle title="Emne:">
                <Text>
                  {capitalizedModalTitle} for møtet {meeting.name}
                </Text>
              </McTitle>
              <McTitle title="Innhold:">
                <Textarea
                  autosize
                  minRows={4}
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.currentTarget.value)}
                />
                <Text mt={"xs"} style={{ overflowWrap: "anywhere" }}>
                  Link til møte: <Anchor href={url} style={{ overflowWrap: "anywhere" }}>{url}</Anchor>
                </Text>
                <Text size="xs" c="dimmed" mt={"xs"}>
                  Denne e-posten ble sendt ut fra MCApps av {user.name} og kan ikke besvares.
                </Text>
              </McTitle>
              <Divider />
            </Stack>
          )}
          {sendEmailError == null && isSuccess === false && (
            <Stack align="center" gap="sm">
              <Text size="sm" c="dimmed" style={{ fontStyle: "italic" }}>
                Denne e-posten sendes til alle {meeting?.participants.length + meeting?.externalParticipants?.length}{" "}
                deltakere
              </Text>
              <Button
                size="md"
                w={"180px"}
                variant="filled"
                leftSection={<IconSend size={16} />}
                onClick={() => {
                  sendMeetingAgnedaByMail({
                    body: {
                      emailContent: emailContent,
                      emailSubject: emailSubject,
                      urlToMeetingAgenda: url,
                      contextLabel: modalTitle,
                    },
                    params: { projectId: meeting.project, meetingId: meeting._id },
                  });
                }}
                disabled={sendMeetingAgendaMail}
              >
                {sendMeetingAgendaMail ? "Sender..." : "Send"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>
    </Modal>
  );
};

export default SendMeetingAgendaByMailModal;
